/**
 * Where a lens job's invoice goes in Xero, in the job dialogs' invoice card: the user has to be logged
 * in to Xero for the practice, and the patient linked to a Xero contact. Each is checked, and put right
 * here, so invoicing is one step.
 *
 * The contact is worked out for the user: a patient with no linked contact is looked up in Xero by
 * name. One contact of exactly that name is linked, and none at all means a new contact is made - both
 * only when the invoice is created, so a quote never touches Xero. Only when it can't be told (several
 * possible contacts) does the user choose.
 *
 * Xero never holds up the job. Until it is ready the user can log in, or invoice without Xero: the
 * invoice is kept in CatTrax, and the job goes on to its order as any other.
 *
 * Reports through `status`: `ready` once the invoice can be raised, `skipped` once the user chose to
 * invoice without Xero, `prepare()` to link or make the contact first (resolves once it is in place),
 * and `check()` to look again (the dialog calls it when Xero refuses an invoice).
 *
 * Given a `job` already invoiced in CatTrax only, it raises that invoice in Xero instead - with any
 * payments taken since, into the bank account chosen here - and calls `onRaised` with the job.
 */
app.component("lensXeroInvoicing", {
	templateUrl: "app/src/js/components/lensDispensing/lensXeroInvoicing.html",
	controller: "lensXeroInvoicingController",
	bindings: {
		patient: "<",
		status: "=?",
		job: "<?",
		onRaised: "&?",
	},
});

app.controller("lensXeroInvoicingController", [
	"$scope",
	"$q",
	"$window",
	"Session",
	"BillingAPI",
	"LensDispensingStore",
	"toast",
	function ($scope, $q, $window, Session, BillingAPI, Store, toast) {
		const $ctrl = this;
		// later (a job kept in CatTrax, not yet looked at) | checking | login | conflict | unreachable |
		// contact (the user chooses) | auto (linked or made on invoicing) | archived | ready
		$scope.step = "checking";
		// Invoicing without Xero, chosen by the user while Xero isn't ready.
		$scope.skipped = false;
		$scope.contact = null;
		// What happens to the contact on invoicing: { kind: "link", contact } or { kind: "create" }.
		$scope.plan = null;
		$scope.search = { term: "", results: null, busy: false, error: null };
		$scope.busy = false;

		const patientName = () => [$ctrl.patient.firstName, $ctrl.patient.lastName].filter(Boolean).join(" ");
		$scope.patientName = patientName;
		const sameName = (a, b) => String(a || "").trim().replace(/\s+/g, " ").toLowerCase() === String(b || "").trim().replace(/\s+/g, " ").toLowerCase();

		function report() {
			$ctrl.status.ready = $scope.step === "ready" || $scope.step === "auto";
			$ctrl.status.step = $scope.step;
			$ctrl.status.skipped = $scope.skipped;
		}

		// Invoice without Xero: kept in CatTrax, and raised in Xero from the job later.
		$scope.skip = function () {
			$scope.skipped = true;
			report();
		};
		$scope.useXero = function () {
			$scope.skipped = false;
			report();
		};

		// Looks the patient up in Xero by name, and decides what to do with no one asked where it can.
		function planContact() {
			const name = patientName();
			$scope.search.term = name;
			$scope.plan = null;
			if (name.length < 3) return ($scope.step = "contact");
			return BillingAPI.getContact($ctrl.patient.id, name).then(
				function (r) {
					const results = r.data || [];
					const named = results.filter((c) => sameName(c.name, name));
					$scope.search.results = results;
					if (named.length === 1 && !named[0].linked_to_other_patient) $scope.plan = { kind: "link", contact: named[0] };
					else if (!results.length) $scope.plan = { kind: "create" };
					$scope.step = $scope.plan ? "auto" : "contact";
				},
				function () {
					$scope.search.error = "Xero's contacts couldn't be searched.";
					$scope.step = "contact";
				}
			);
		}

		function check() {
			$scope.step = "checking";
			report();
			return BillingAPI.getLoggedIn()
				.then(function (resp) {
					const s = resp.data || {};
					if (!s.logged_in_to) return ($scope.step = "login");
					if (s.error_organisation_conflict) return ($scope.step = "conflict");
					$scope.organisation = s.logged_in_to;
					if ($ctrl.job) chooseAccount(s.accounts);
					return BillingAPI.getContact($ctrl.patient.id, "").then(function (r) {
						const linked = (r.data || []).find((c) => c.linked_to_this_patient);
						$scope.contact = linked || null;
						$scope.plan = null;
						// Xero refuses an invoice to an archived contact, so it isn't ready until restored.
						if (linked) return ($scope.step = linked.archived ? "archived" : "ready");
						return planContact();
					});
				})
				.catch((resp) => ($scope.step = resp && resp.status === 401 ? "login" : "unreachable"))
				.finally(report);
		}

		$ctrl.$onInit = function () {
			$ctrl.status = $ctrl.status || {};
			$ctrl.status.check = check;
			$ctrl.status.prepare = prepare;
			// A job kept in CatTrax waits for the user to ask, rather than reaching Xero every time it opens.
			if ($ctrl.job) {
				$scope.step = "later";
				report();
			} else {
				check();
			}
		};

		// Links or makes the contact decided on, ahead of the invoice. Resolves once the patient has one.
		function prepare() {
			if ($scope.step !== "auto" || !$scope.plan) return $q.resolve();
			const plan = $scope.plan;
			const made = plan.kind === "link" ? BillingAPI.linkContract($ctrl.patient.id, plan.contact.contact_id) : BillingAPI.registerContact($ctrl.patient.id);
			return made
				.then(check, () => $q.reject({ data: { clientMessage: plan.kind === "link" ? "The patient couldn't be linked to their Xero contact." : "The patient's Xero contact couldn't be created." } }))
				.then(() => ($scope.step === "ready" ? null : $q.reject({ data: { clientMessage: "The patient's Xero contact isn't ready - see the Xero panel." } })));
		}

		// Not the contact it found: the user chooses from the search instead.
		$scope.chooseAnother = function () {
			$scope.plan = null;
			$scope.step = "contact";
			report();
		};

		$scope.check = check;

		// ------------------------------------------------------------------
		// Raising a job kept in CatTrax in Xero
		// ------------------------------------------------------------------
		// The payments taken in CatTrax go into Xero with the invoice, into one of the organisation's bank
		// accounts - the one last used for a lens payment at this practice, to start with.
		$scope.account = { list: [], id: null };
		const accountKey = () => "cattrax.lensPaymentAccount." + ((Session.practice && Session.practice.id) || "");

		function chooseAccount(accounts) {
			$scope.account.list = (accounts || []).filter((a) => a.account_class === "BANK");
			let remembered = null;
			try {
				remembered = window.localStorage.getItem(accountKey());
			} catch (e) {
				remembered = null;
			}
			const chosen = $scope.account.list.find((a) => a.account_id === remembered) || $scope.account.list[0];
			$scope.account.id = chosen ? chosen.account_id : null;
		}

		$scope.hasPayments = () => !!$ctrl.job && !!$ctrl.job.invoice && ($ctrl.job.invoice.payments || []).length > 0;
		$scope.canRaise = () => ($scope.step === "ready" || $scope.step === "auto") && !$scope.busy && (!$scope.hasPayments() || !!$scope.account.id);

		$scope.raise = function () {
			if (!$scope.canRaise()) return;
			$scope.busy = true;
			prepare()
				.then(() => Store.raiseInXero($ctrl.patient.id, $ctrl.job, $scope.hasPayments() ? $scope.account.id : null))
				.then(function (saved) {
					const inv = saved.invoice || {};
					toast.create({
						timeout: 6000, dismissible: true,
						className: inv.xeroStatus === "DRAFT" ? "alert-warning" : "alert-success",
						message: inv.xeroStatus === "DRAFT"
							? `Invoice ${inv.number || ""} is in Xero, but couldn't be approved - it's waiting as a draft in the patient's invoices.`
							: `Invoice ${inv.number || ""} is in Xero.`,
					});
					if ($ctrl.onRaised) $ctrl.onRaised({ job: saved });
				})
				.catch(function (resp) {
					if (resp && resp.status === 401) check();
					toast.create({ timeout: 6000, message: Store.errorMessage(resp, "The invoice couldn't be raised in Xero."), className: "alert-danger", dismissible: true });
				})
				.finally(() => ($scope.busy = false));
		};

		// Xero's login opens in its own window; coming back to this one looks again.
		$scope.login = function () {
			$scope.useXero();
			BillingAPI.getXeroLoginUrl().then(function (resp) {
				$window.open(resp.data, "_blank", "location=no,toolbar=yes,scrollbars=yes,resizable=yes,top=200,left=500,width=500,height=600");
			});
		};
		function onFocus() {
			if ($scope.step === "login" && !$scope.skipped) $scope.$applyAsync(check);
		}
		$window.addEventListener("focus", onFocus);
		$scope.$on("$destroy", () => $window.removeEventListener("focus", onFocus));

		$scope.find = function () {
			const term = ($scope.search.term || "").trim();
			if (term.length < 3) {
				$scope.search.results = null;
				return;
			}
			$scope.search.busy = true;
			$scope.search.error = null;
			BillingAPI.getContact($ctrl.patient.id, term)
				.then((r) => ($scope.search.results = r.data || []))
				.catch(() => ($scope.search.error = "Xero's contacts couldn't be searched."))
				.finally(() => ($scope.search.busy = false));
		};

		$scope.link = function (contact) {
			if ($scope.busy) return;
			$scope.busy = true;
			BillingAPI.linkContract($ctrl.patient.id, contact.contact_id)
				.then(check)
				.catch(() => ($scope.search.error = "The contact couldn't be linked."))
				.finally(() => ($scope.busy = false));
		};

		// A new Xero contact from the patient's details, linked to them.
		$scope.create = function () {
			if ($scope.busy) return;
			$scope.busy = true;
			BillingAPI.registerContact($ctrl.patient.id)
				.then(check)
				.catch(() => ($scope.search.error = "The contact couldn't be created in Xero."))
				.finally(() => ($scope.busy = false));
		};
	},
]);
